import { forwardRef } from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";

import { platformProps } from "../../lib";
import { spacing } from "../../tokens";
import type {
  PopoverContentPrimitiveProps,
  PopoverRootPrimitiveProps,
  PopoverTriggerPrimitiveProps,
} from "./types";

/** Ref types, resolved per platform like the components. */
export type PopoverTriggerRef = HTMLButtonElement;
export type PopoverContentRef = HTMLDivElement;

/**
 * Popover Root - Web implementation (Radix)
 *
 * Unstyled primitive. Styling is applied by the atom layer via className.
 */
const PopoverRoot = ({
  open,
  defaultOpen,
  onOpenChange,
  modal,
  children,
}: PopoverRootPrimitiveProps) => (
  <PopoverPrimitive.Root
    open={open}
    defaultOpen={defaultOpen}
    onOpenChange={onOpenChange}
    modal={modal}
  >
    {children}
  </PopoverPrimitive.Root>
);

PopoverRoot.displayName = PopoverPrimitive.Root.displayName;

/**
 * Popover Trigger - Web implementation (Radix) *
 * Typed by its shared props only; what a parent's `asChild` Slot injects
 * (handlers, `disabled`) still passes through to the library.
 */
const PopoverTrigger = forwardRef<PopoverTriggerRef, PopoverTriggerPrimitiveProps>(
  ({ asChild, className, children, testID, accessibilityLabel, ...injected }, ref) => (
    <PopoverPrimitive.Trigger
      ref={ref}
      asChild={asChild}
      // Only when set: with asChild an undefined aria-label reaches the child's
      // props and can override the label it renders itself
      {...(accessibilityLabel !== undefined && { "aria-label": accessibilityLabel })}
      className={className}
      {...platformProps({ testID, slot: "popover-trigger" })}
      {...injected}
    >
      {children}
    </PopoverPrimitive.Trigger>
  ),
);

PopoverTrigger.displayName = PopoverPrimitive.Trigger.displayName;

/**
 * Popover Content - Web implementation (Radix)
 *
 * Wrapped in a Portal so positioning is isolated from parent overflow/clipping.
 */
const PopoverContent = forwardRef<PopoverContentRef, PopoverContentPrimitiveProps>(
  (
    {
      side = "bottom",
      align = "start",
      sideOffset = 8,
      alignOffset,
      className,
      children,
      testID,
      accessibilityLabel,
      ...injected
    },
    ref,
  ) => (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        ref={ref}
        side={side}
        align={align}
        sideOffset={sideOffset}
        alignOffset={alignOffset}
        // Keeps clear of the viewport edges when it shifts or flips to fit
        collisionPadding={spacing.lg}
        aria-label={accessibilityLabel}
        className={className}
        {...platformProps({ testID, slot: "popover-content" })}
        {...injected}
      >
        {children}
      </PopoverPrimitive.Content>
    </PopoverPrimitive.Portal>
  ),
);

PopoverContent.displayName = PopoverPrimitive.Content.displayName;

export { PopoverRoot, PopoverTrigger, PopoverContent };
